import PDFPage from './PDFPage'
import type { ReportData, BrandingConfig } from '@/types'
import { formatCurrency } from '@/utils/calculations'
import { ILC_WEALTH_UPLIFT } from '@/data/valueOfAdvice'

interface ExecutiveSummaryPageProps {
  data: ReportData
  branding: BrandingConfig
  pageNumber: number
  totalPages: number
}

export default function ExecutiveSummaryPage({
  data,
  branding,
  pageNumber,
  totalPages,
}: ExecutiveSummaryPageProps) {
  const isLight = branding.theme === 'light'
  const cardBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'
  const cardBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'
  const mutedColor = isLight ? '#64748b' : '#a8a5a0'

  return (
    <PDFPage
      pageNumber={pageNumber}
      totalPages={totalPages}
      title="Executive Summary"
      subtitle={data.clientName || 'Value of Advice Report'}
      branding={branding}
    >
      {/* Hero Stat */}
      <div
        style={{
          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center',
          marginBottom: '24px',
          color: '#1a1f2e',
        }}
      >
        <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px' }}>
          Projected Value of Advice Over {data.years} Years
        </div>
        <div
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: '48px',
            fontWeight: 600,
            lineHeight: 1.1,
          }}
        >
          {formatCurrency(data.results.valueAdded)}
        </div>
        <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '8px' }}>
          Additional wealth created through professional advice
        </div>
      </div>

      {/* Key Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <StatCard
          label="Annual Value Add"
          value="~3%"
          description="Vanguard Adviser's Alpha"
          bgColor={cardBg}
          borderColor={cardBorder}
          accentColor={branding.primaryColor}
        />
        <StatCard
          label="10-Year Wealth Uplift"
          value={formatCurrency(ILC_WEALTH_UPLIFT.overallUplift)}
          description="ILC/Royal London Study"
          bgColor={cardBg}
          borderColor={cardBorder}
          accentColor={branding.primaryColor}
        />
        <StatCard
          label="Higher Pension Wealth"
          value={`${ILC_WEALTH_UPLIFT.ongoingAdviceBonus * 100}%`}
          description="With ongoing advice"
          bgColor={cardBg}
          borderColor={cardBorder}
          accentColor={branding.primaryColor}
        />
      </div>

      {/* Portfolio Assumptions */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>
          Projection Assumptions
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          <AssumptionRow label="Starting Portfolio" value={formatCurrency(data.portfolioValue)} />
          <AssumptionRow
            label="Annual Contribution"
            value={formatCurrency(data.annualContribution)}
          />
          <AssumptionRow label="Investment Horizon" value={`${data.years} years`} />
          <AssumptionRow
            label="Risk Profile"
            value={data.riskProfile.charAt(0).toUpperCase() + data.riskProfile.slice(1)}
          />
        </div>
      </div>

      {/* Comparison */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
        }}
      >
        <div
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '12px', color: mutedColor, marginBottom: '8px' }}>
            Without Advice
          </div>
          <div style={{ fontSize: '28px', fontWeight: 600 }}>
            {formatCurrency(data.results.withoutAdvice)}
          </div>
        </div>
        <div
          style={{
            background: `linear-gradient(135deg, ${branding.primaryColor}20 0%, ${branding.secondaryColor}20 100%)`,
            border: `1px solid ${branding.primaryColor}40`,
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '12px', color: branding.primaryColor, marginBottom: '8px' }}>
            With Advice
          </div>
          <div style={{ fontSize: '28px', fontWeight: 600, color: branding.primaryColor }}>
            {formatCurrency(data.results.withAdvice)}
          </div>
        </div>
      </div>
    </PDFPage>
  )
}

function StatCard({
  label,
  value,
  description,
  bgColor,
  borderColor,
  accentColor,
}: {
  label: string
  value: string
  description: string
  bgColor: string
  borderColor: string
  accentColor: string
}) {
  return (
    <div
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 600, color: accentColor }}>{value}</div>
      <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '4px' }}>{description}</div>
    </div>
  )
}

function AssumptionRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
      <span style={{ opacity: 0.7 }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  )
}
